import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg bg-background-100 border border-background-200/50 hover:border-accent-500/30 transition-all duration-300"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
          <div className="absolute top-3 left-3 flex gap-2">
            {article.isExclusive && (
              <span className="bg-accent-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                独家
              </span>
            )}
            {article.isHot && (
              <span className="bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md">
                热门
              </span>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <h3 className="text-lg md:text-xl font-bold text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <p className="mt-2 text-sm text-foreground-600 line-clamp-2">
              {article.excerpt}
            </p>
            <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-2-line"></i>
                {article.commentCount}
              </span>
              <span>{article.publishDate}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors border border-transparent hover:border-background-200/50"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg bg-background-100 border border-background-200/50 hover:border-accent-500/30 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-t-lg">
        <img
          src={article.imageUrl}
          alt={article.imageAlt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {article.isExclusive && (
            <span className="bg-accent-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
              独家
            </span>
          )}
          {article.isHot && (
            <span className="bg-primary-500 text-background-50 text-xs font-bold px-1.5 py-0.5 rounded">
              热门
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <span className="text-xs text-accent-500 font-medium">
          {article.category}
        </span>
        <h3 className="mt-1.5 text-base font-semibold text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-2-line"></i>
              {article.commentCount}
            </span>
          </div>
          <span>{article.publishDate}</span>
        </div>
        {article.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-foreground-500 bg-background-50 px-2 py-0.5 rounded-full border border-background-200/50"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}